angular.module('rjmNg.api').factory("BaseDefinition", [
  'utils'
  'ceDefaultDefinitions'
  'app'
  'restrictionSetService'
  'operatorEnums'
  (
    utils
    ceDefaultDefinitions
    app
    restrictionSetService
    operatorEnums
  ) ->

    # Verify that the filter has a valid operator and return that operator.
    #
    # Throws an error in the event of an invalid filter or operator.
    getColumnEditorFilter = (filter) ->

      if not filter.operation?
        throw "A filter is missing an operation."

      operation =
        operatorEnums.SCHEMA_SERVICE_COMPARISON_OPERATOR_IDS[filter.operation]

      if not operation?
        validOperators =
          Object.keys(operatorEnums.SCHEMA_SERVICE_COMPARISON_OPERATOR_IDS).map (key) ->
            '"' + key + '"'
        throw "A filter's operation was invalid: expected one of
               (#{validOperators.join(', ')}); got
               \"#{filter.operation}\""

      return operation

    getSchemaServiceFilter = (restriction) ->
      operatorEnums.SCHEMA_SERVICE_COMPARISON_OPERATOR_SYMBOLS[restriction.op]

    checkColumnEditorRestriction =
      (structure, supportedTableIds, restrictionColumnId) ->
        return unless supportedTableIds?

        restrictionTableId = structure.columns[restrictionColumnId].table_id

        if supportedTableIds.indexOf(restrictionTableId) is -1
          throw "A filter contains a column from an table
                 (#{structure.tables[restrictionTableId].name}) which is not valid
                 for this definition."

    # Returns false if the filter_set doesn't have any restrictions.
    # Throws an error if filter_set is malformed.
    validateSchemaServiceRestriction = (filter_set) ->
      return false unless filter_set?

      hasRestrictions = filter_set.filters? and filter_set.filters.length > 0
      hasSequence = filter_set.sequence? and filter_set.sequence.length > 0

      if hasRestrictions and not hasSequence
        throw "The filter_set is missing the sequence property."
      else if not hasRestrictions and hasSequence
        throw "The filter_set is missing the filters property."
      else
        for filter in filter_set.filters when not filter.filter_set_id?
          if not filter.left?.column_id?
            throw "A filter is missing the left.column_id attribute."
          if not filter.right?
            throw "A filter does not have the right attribute set."
          if not app.hasFeatureFlag("RJQL_SERVICE_CLIENT") and not filter.right?.hasOwnProperty('value')
            throw "A filter does not have the right.value attribute set."
      return hasRestrictions

    class BaseDefinition

      @isSupported: (column = null) ->
        # Make sure the column doesn't contain any unsupported filters
        if column?.properties?
          try
            validateSchemaServiceRestriction(column.properties.filter_set)
          catch
            return false

        # Allows a definition to be hidden behind feature flags, by setting the
        # featureFlag property.
        return not @featureFlag or app.hasFeatureFlag(@featureFlag)

      # Attempts to convert filters for an existing column into a format useable
      # by the restrictionSetDirective. Returns the filter_set object, or null
      # if no filter set is set.
      #
      # Throws an error (a string) if the column's filters cannot be converted.
      @loadFilters: (structure, column) ->
        return undefined unless @filtersSupported and column.properties?.filter_set?

        unless validateSchemaServiceRestriction(column.properties.filter_set)
          return undefined

        filterSet =
          restrictions: column.properties.filter_set.filters.map (filter) ->

            if filter.filter_set_id?
              filter_set_id: filter.filter_set_id
            else
              leftColumn = structure.columns[filter.left.column_id]
              if filter.right.column_id?
                rightColumn = structure.columns[filter.right.column_id]
                rightFilter =
                  column_id: "#{filter.right.column_id}"
                  datatype: rightColumn.old_data_type
              else
                rightFilter = filter.right.value
              left:
                column_id: "#{filter.left.column_id}"
                datatype: if leftColumn? then leftColumn.old_data_type else undefined
              op: getColumnEditorFilter(filter)
              right: rightFilter

          sequence:
            string: column.properties.filter_set.sequence

        restrictionSetService.validateAndUpdateSyntaxTree(filterSet)

        return filterSet

      @fromColumn: (structure, column, definition = null) ->
        field = utils.copy ceDefaultDefinitions.field
        field.name = column.name
        field.id = column.id
        field.attrs.ref.table = column.table_id
        field.attrs.ref.column = column.properties.source_column_id
        field.definition = definition
        filter_set = @loadFilters(structure, column)

        if filter_set?
          field.filter_set = filter_set

        return field

      # Set the value of properties.filter_set
      @setFilters: (structure, table, column, field) ->

        return unless @filtersSupported

        column.properties ?= {}

        # If the filter_set property isn't set on the field, add an empty filter
        # set
        unless field.filter_set?
          column.properties.filter_set =
            filters: []
            sequence: ''

          return

        # Make sure the syntaxTree is up to date
        restrictionSetService.validateAndUpdateSyntaxTree(field.filter_set)

        # Make sure the filters are valid
        unless restrictionSetService.restrictionSetIsValid(field.filter_set)
          throw "This column's filters are invalid."

        supportedRestrictionTableIds =
          @getSupportedRestrictionTableIds(table, field)

        column.properties.filter_set =
          filters: field.filter_set.restrictions.map (restriction) ->

            if restriction.filter_set_id?
              # This is a filter set

              # The select2 directive in the filterSet directive seems to set all
              # ids to strings, so ensure that this is an integer.
              filter_set_id: +restriction.filter_set_id

            else
              restrictionColumnId = +restriction.left.column_id

              checkColumnEditorRestriction(
                structure
                supportedRestrictionTableIds
                restrictionColumnId
              )

              # handle case where the right part of the filter is referencing a column
              if restriction.right instanceof Object and not Array.isArray(restriction.right)
                right =
                  column_id: +restriction.right.column_id
              else
                right =
                  value: switch restriction.right
                    when operatorEnums.NULL then null
                    else restriction.right

              # This is a restriction
              left:
                column_id: restrictionColumnId
              operation: getSchemaServiceFilter(restriction)
              right: right

          sequence: field.filter_set.sequence.string

      @toColumn: (structure, table, field) ->

        column = structure.columns[field.id]

        if column?
          result = utils.copy column
        else
          result = utils.copy ceDefaultDefinitions.column

        @setFilters(structure, table, result, field)

        result.table_id = table
        result.name = field.name
        result.id = field.id

        return result

      @validateReference: (localTableId, table, field, reference) ->
        return false

      @getSupportedRestrictionTableIds = (localTableId, field) -> null

      @getFilterSetProperties: (localTableId, field) ->
        tableIds: @getSupportedRetrictionTableIds(localTableId, field)
        warning: null

      @getSubtype: (subtype) ->
        _.find @subtypes, (subtypeDefinition) -> subtypeDefinition.subtype is subtype

      @getArg: (subtype) ->
        if subtype?
          subtypeDefinition = @getSubtype(subtype)
          if subtypeDefinition?.arg?
            return subtypeDefinition.arg

        unless @arg?
          throw "The column doesn't have a operand type."

        return @arg

      @validateField: (column) ->

        unless column.definition?
          throw "The column definition is not set."


        _definitionNeedsReference = ['relocated_field', 'aggregate']

        if (_definitionNeedsReference.some (definition) -> definition is column.definition)
          arg = @getArg(column.subtype)
          if arg is 'table' and not column.attrs?.ref?.table?
            throw "The operand table is not set."
          else if (arg is 'tableColumn' or arg is 'column') and not column.attrs?.ref?.column?
            throw "The operand column is not set."

          unless column.attrs?.reference?
            throw "You'll need to choose or create a path."

  ]
)
